import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";

import PanelTemplate from "../../../templates/Panel";
import PageHeader from "../../../components/PageHeader";
import PageFooter from "../../../components/PageFooter";
import Box from "../../../components/Box";
import BoxTabs from "../../../components/BoxTabs";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import InputTextarea from "../../../components/InputTextarea";
import Modal from "../../../components/Modal";
import Table from "../../../components/Table";
import Toggle from "../../../components/Toggle";
import { ModalDelete } from "../../../components/ModalDelete";
import TaxRuleFields from "../components/TaxRuleFields";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { hasPermission } from "../../../services/auth";
import {
    FINAL_CONSUMER_OPTIONS,
    FISCAL_ORIGINS,
    formatCfop,
    OPERATION_DIRECTIONS,
    OPERATION_PURPOSES,
    OPERATION_TAXES,
    STATE_OPTIONS,
    REGIME_TAXES,
    RULE_REGIMES,
} from "../helpers";

const emptyRule = tax => ({
    tax,
    is_exception: false,
    cst: '',
    cfop: '',
    rate: '',
    base: '',
    attributes: {},
    notes: '',
    fisco_notes: '',
    states: [],
    ncms: [],
    fiscal_origins: [],
});

const emptyForm = () => ({
    name: '',
    direction: 'out',
    purpose: 'normal',
    final_consumer: 'auto',
    cfop_internal: '',
    cfop_interstate: '',
    notes: '',
    fisco_notes: '',
    settings: {},
    active: true,
    is_default: false,
});

// A API guarda null para "decide pelo cliente"; o select trabalha com texto.
const finalConsumerToForm = value => value === null || value === undefined ? 'auto' : (value ? 'yes' : 'no');
const finalConsumerToApi = value => value === 'auto' ? null : value === 'yes';

const onlyDigits = value => (value ?? '').toString().replace(/\D/g, '');

/**
 * Cadastro de uma natureza de operação (BDM-887).
 *
 * O topo é o que a operação É (nome, tipo, finalidade, CFOP); as abas são
 * como ela é TRIBUTADA, uma por imposto. Simples e ICMS convivem porque a
 * mesma natureza serve a empresas de regimes diferentes: vale o CSOSN para
 * quem é do Simples e o CST para quem não é.
 *
 * Cada aba tem, além da regra base, as exceções — "para estes estados e/ou
 * estes NCMs, a tributação é outra". É como o mercado resolve ST, que muda
 * de estado para estado.
 */
export const FiscalOperationEdit = () => {
    const { id } = useParams();
    const isCreating = !id;

    const [loading, setLoading] = useState(!isCreating);
    const [saving, setSaving] = useState(false);
    // Erros por campo devolvidos pelo backend no último salvamento.
    const [errors, setErrors] = useState({});
    const [tab, setTab] = useState(0);

    const [form, setForm] = useState(emptyForm());
    const [rules, setRules] = useState({});
    const [exceptions, setExceptions] = useState({});
    // Regras próprias por família de regime, hoje só de PIS/COFINS:
    // { pis: { simples: regra|null, normal: regra|null } }. Nulo = não existe,
    // e aí vale a regra geral acima — nada é assumido por ninguém.
    const [regimeRules, setRegimeRules] = useState({});

    // Tabela oficial de cClassTrib, vinda do backend — a MESMA que valida o
    // salvamento. Uma cópia no front divergiria dela, e a tela passaria a
    // oferecer código que o backend recusa.
    const [classifications, setClassifications] = useState([]);

    // Exceção sendo criada/editada no modal: {tax, index, rule}
    const [editingException, setEditingException] = useState(null);
    const [exceptionToDelete, setExceptionToDelete] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const canManage = hasPermission('manage_fiscal');

    const returnTo = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('return_to') ?? '/fiscal/operations';
    }, [location.search]);

    useEffect(() => {
        api.get(`/fiscal-operation/tax-classifications`)
            .then(res => setClassifications(res.data.resources ?? []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (isCreating) {
            setRules(Object.fromEntries(OPERATION_TAXES.map(({ key }) => [key, emptyRule(key)])));
            setExceptions(Object.fromEntries(OPERATION_TAXES.map(({ key }) => [key, []])));
            setRegimeRules(Object.fromEntries(REGIME_TAXES.map(key => [key, {}])));
            return;
        }

        getOperation();
    }, [id]);

    const getOperation = () => {
        setLoading(true);

        api.get(`/fiscal-operation/${id}`).then(res => {
            const resource = res.data.resource;

            setForm({
                name: resource.name ?? '',
                direction: resource.direction ?? 'out',
                purpose: resource.purpose ?? 'normal',
                final_consumer: finalConsumerToForm(resource.final_consumer),
                cfop_internal: resource.cfop_internal ?? '',
                cfop_interstate: resource.cfop_interstate ?? '',
                notes: resource.notes ?? '',
                fisco_notes: resource.fisco_notes ?? '',
                settings: resource.settings ?? {},
                active: resource.active !== false,
                is_default: resource.is_default === true,
            });

            const base = {};
            const byTax = {};
            const byRegime = {};

            OPERATION_TAXES.forEach(({ key }) => {
                base[key] = emptyRule(key);
                byTax[key] = [];
            });
            REGIME_TAXES.forEach(key => { byRegime[key] = {}; });

            (resource.rules ?? []).forEach(rule => {
                const parsed = {
                    tax: rule.tax,
                    regime: rule.regime ?? null,
                    is_exception: rule.is_exception === true,
                    cst: rule.cst ?? '',
                    cfop: rule.cfop ?? '',
                    rate: rule.rate ?? '',
                    base: rule.base ?? '',
                    attributes: rule.attributes ?? {},
                    notes: rule.notes ?? '',
                    fisco_notes: rule.fisco_notes ?? '',
                    states: rule.states ?? [],
                    ncms: rule.ncms ?? [],
                    fiscal_origins: rule.fiscal_origins ?? [],
                };

                if (parsed.is_exception) {
                    byTax[rule.tax] = [...(byTax[rule.tax] ?? []), parsed];
                } else if (parsed.regime) {
                    byRegime[rule.tax] = { ...(byRegime[rule.tax] ?? {}), [parsed.regime]: parsed };
                } else {
                    base[rule.tax] = parsed;
                }
            });

            setRules(base);
            setExceptions(byTax);
            setRegimeRules(byRegime);
        }).catch(renderError).then(() => setLoading(false));
    };

    const updateForm = changes => setForm(current => ({ ...current, ...changes }));

    const updateSetting = (key, value) => setForm(current => ({
        ...current,
        settings: { ...current.settings, [key]: value },
    }));

    const updateRule = (tax, changes) => setRules(current => ({
        ...current,
        [tax]: { ...current[tax], ...changes },
    }));

    const regimeRule = (tax, regime) => regimeRules[tax]?.[regime] ?? null;

    // Criar a regra do regime NÃO copia nada da regra geral: assumir que o
    // que estava lá era do Simples seria um palpite, e palpite em tributação
    // é o que a gente não faz. Nasce vazia para o usuário preencher.
    const toggleRegimeRule = (tax, regime) => setRegimeRules(current => {
        const forTax = { ...(current[tax] ?? {}) };

        forTax[regime] = forTax[regime] ? null : { ...emptyRule(tax), regime };

        return { ...current, [tax]: forTax };
    });

    const updateRegimeRule = (tax, regime, changes) => setRegimeRules(current => ({
        ...current,
        [tax]: {
            ...(current[tax] ?? {}),
            [regime]: { ...(current[tax]?.[regime] ?? emptyRule(tax)), ...changes },
        },
    }));

    const updateRegimeRuleAttribute = (tax, regime, key, value) => setRegimeRules(current => ({
        ...current,
        [tax]: {
            ...(current[tax] ?? {}),
            [regime]: {
                ...(current[tax]?.[regime] ?? emptyRule(tax)),
                attributes: { ...(current[tax]?.[regime]?.attributes ?? {}), [key]: value },
            },
        },
    }));

    const updateRuleAttribute = (tax, key, value) => setRules(current => ({
        ...current,
        [tax]: {
            ...current[tax],
            attributes: { ...current[tax].attributes, [key]: value },
        },
    }));

    const save = () => {
        setSaving(true);
        setErrors({});

        const payload = {
            ...form,
            final_consumer: finalConsumerToApi(form.final_consumer),
            cfop_internal: form.cfop_internal === '' ? null : parseInt(form.cfop_internal, 10),
            cfop_interstate: form.cfop_interstate === '' ? null : parseInt(form.cfop_interstate, 10),
            rules: [
                ...Object.values(rules).map(rule => serializeRule(rule)),
                // Regras por regime: só as que o usuário criou de fato.
                ...Object.entries(regimeRules).flatMap(([tax, byRegime]) =>
                    Object.entries(byRegime ?? {})
                        .filter(([, rule]) => !!rule)
                        .map(([regime, rule]) => serializeRule({ ...rule, tax, regime }))
                ),
                ...Object.entries(exceptions).flatMap(([tax, list]) =>
                    list.map((rule, index) => serializeRule({ ...rule, tax, is_exception: true, position: index }))
                ),
            ],
        };

        const request = isCreating
            ? api.post('/fiscal-operation', payload)
            : api.put(`/fiscal-operation/${id}`, payload);

        request.then(res => {
            toast.success('Natureza de operação salva');

            if (isCreating) {
                navigate(`/fiscal/operations/${res.data.resource.id}`);
                return;
            }

            getOperation();
        }).catch(error => {
            const fields = error.response?.data?.fields;

            if (!fields) {
                renderError(error);
                return;
            }

            setErrors(fields);

            // O erro de uma regra chega como `rules.N`: sem dizer de qual
            // imposto (e se é exceção) a mensagem não leva a lugar nenhum.
            Object.entries(fields).forEach(([field, messages]) => {
                const index = field.match(/^rules\.(\d+)/)?.[1];
                const rule = index !== undefined ? payload.rules[index] : null;
                const where = rule
                    ? `${OPERATION_TAXES.find(tax => tax.key === rule.tax)?.label ?? rule.tax}${rule.is_exception ? ' (exceção)' : rule.regime ? ' (regra do regime)' : ''}: `
                    : '';

                messages.forEach(message => toast.error(where + message));
            });
        }).then(() => setSaving(false));
    };

    const serializeRule = rule => ({
        tax: rule.tax,
        regime: rule.regime ?? null,
        is_exception: rule.is_exception === true,
        position: rule.position ?? 0,
        states: rule.states ?? [],
        ncms: (rule.ncms ?? []).map(onlyDigits).filter(Boolean),
        fiscal_origins: rule.fiscal_origins ?? [],
        cst: rule.cst === '' ? null : rule.cst,
        cfop: rule.cfop === '' || rule.cfop === null ? null : parseInt(rule.cfop, 10),
        rate: rule.rate === '' ? null : parseFloat(String(rule.rate).replace(',', '.')),
        base: rule.base === '' ? null : parseFloat(String(rule.base).replace(',', '.')),
        attributes: Object.fromEntries(
            Object.entries(rule.attributes ?? {}).filter(([, value]) => value !== '' && value !== null && value !== undefined)
        ),
        notes: rule.notes === '' ? null : rule.notes,
        fisco_notes: rule.fisco_notes === '' ? null : rule.fisco_notes,
    });

    const setAsDefault = () => {
        setSaving(true);

        api.patch(`/fiscal-operation/${id}/default`).then(() => {
            toast.success('Esta passou a ser a natureza padrão das notas');
            updateForm({ is_default: true });
        }).catch(renderError).then(() => setSaving(false));
    };

    const saveException = () => {
        const { tax, index, rule } = editingException;

        setExceptions(current => {
            const list = [...(current[tax] ?? [])];

            if (index === null) {
                list.push(rule);
            } else {
                list[index] = rule;
            }

            return { ...current, [tax]: list };
        });

        setEditingException(null);
    };

    const removeException = () => {
        const { tax, index } = exceptionToDelete;

        setExceptions(current => ({
            ...current,
            [tax]: (current[tax] ?? []).filter((_, position) => position !== index),
        }));

        setExceptionToDelete(null);
    };

    // ————— campos de tributação, compartilhados entre a regra base e a exceção

    const exceptionRows = tax => {
        const list = exceptions[tax] ?? [];

        return list.map((rule, index) => ({
            data: [
                (rule.states ?? []).length ? rule.states.join(', ') : 'Qualquer destino',
                (rule.ncms ?? []).length ? rule.ncms.join(', ') : 'Qualquer produto',
                formatCfop(rule.cfop) || '—',
                rule.cst || '—',
            ],
            action: canManage ? () => setEditingException({ tax, index, rule }) : undefined,
            buttons: canManage ? [
                <Button type={`delete`} svg={`delete`} action={() => setExceptionToDelete({ tax, index })} />
            ] : [],
            id: `${tax}-${index}`,
        }));
    };

    const taxTab = tax => (
        <div className="tax-tab">
            <TaxRuleFields
                classifications={classifications}
                tax={tax}
                rule={rules[tax] ?? emptyRule(tax)}
                direction={form.direction}
                onChange={(kind, key, value) =>
                    kind === 'attribute' ? updateRuleAttribute(tax, key, value) : updateRule(tax, { [key]: value })
                }
            />

            <div className="row">
                <div className="col-6">
                    <Input
                        label={`Observações deste imposto`}
                        value={rules[tax]?.notes ?? ''}
                        change={value => updateRule(tax, { notes: value })}
                    />
                    <p className="input-hint">Sai nas informações complementares da nota</p>
                </div>
                <div className="col-6">
                    <Input
                        label={`Informações ao Fisco`}
                        value={rules[tax]?.fisco_notes ?? ''}
                        change={value => updateRule(tax, { fisco_notes: value })}
                    />
                </div>
            </div>

            {/* PIS/COFINS são os únicos impostos que mudam de CST conforme o
                regime do EMITENTE. Como a natureza é do tenant e o tenant
                pode ter emitentes de regimes diferentes, uma regra só não
                atende os dois. O ICMS já resolve isso com CSOSN e CST
                convivendo como impostos separados. */}
            {REGIME_TAXES.includes(tax) &&
                <div className="section regime-rules">
                    <h4>Regras por regime do emitente</h4>
                    <p className="input-hint">
                        A regra acima vale para qualquer emitente. Se as suas empresas têm
                        regimes diferentes — uma no Simples e outra no Lucro Presumido, por
                        exemplo —, crie aqui a regra própria de cada uma: ela ganha da regra
                        geral só para os emitentes daquele regime.
                    </p>

                    {RULE_REGIMES.map(({ key, label }) => {
                        const rule = regimeRule(tax, key);

                        return (
                            <div className="regime-rule" key={key}>
                                <div className="regime-rule-head">
                                    <strong>{label}</strong>
                                    {canManage &&
                                        <Button
                                            type={`secondary`}
                                            size={`small`}
                                            text={rule ? 'Remover regra' : 'Criar regra própria'}
                                            action={() => toggleRegimeRule(tax, key)}
                                        />
                                    }
                                </div>

                                {rule
                                    ? <TaxRuleFields
                                        classifications={classifications}
                                        tax={tax}
                                        rule={rule}
                                        direction={form.direction}
                                        onChange={(kind, field, value) =>
                                            kind === 'attribute'
                                                ? updateRegimeRuleAttribute(tax, key, field, value)
                                                : updateRegimeRule(tax, key, { [field]: value })
                                        }
                                    />
                                    : <p className="input-hint">
                                        Sem regra própria: vale a regra geral acima.
                                    </p>
                                }
                            </div>
                        );
                    })}
                </div>
            }

            <div className="section exceptions">
                <h4>Exceções</h4>
                <p className="input-hint">
                    Quando a tributação muda para certos destinos ou produtos. Vale a primeira
                    exceção que combinar com o item da nota.
                </p>

                <Table
                    header={['Destinos', 'NCMs', 'CFOP', 'Situação tributária', '']}
                    rows={exceptionRows(tax)}
                />

                {canManage &&
                    <Button
                        type={`secondary`}
                        size={`small`}
                        svg={`plus-circle`}
                        text={`Adicionar exceção`}
                        action={() => setEditingException({
                            tax,
                            index: null,
                            rule: { ...emptyRule(tax), is_exception: true },
                        })}
                    />
                }
            </div>
        </div>
    );

    return (
        <>
            <ModalDelete
                show={exceptionToDelete !== null}
                text={<p>Remover esta exceção?</p>}
                action={removeException}
                close={() => setExceptionToDelete(null)}
            />

            <Modal
                title={editingException?.index === null ? `Adicionar exceção` : `Editar exceção`}
                size={`big`}
                show={editingException !== null}
                close={() => setEditingException(null)}
                footer={
                    <Button
                        type={`primary`}
                        size={`small`}
                        text={`Salvar exceção`}
                        svg={`save-white`}
                        action={saveException}
                    />
                }
            >
                {editingException &&
                    <div className="section">
                        <div className="row">
                            <div className="col-6">
                                <label className="input-label">Estados de destino</label>
                                <Select
                                    isMulti
                                    placeholder="Qualquer destino"
                                    options={STATE_OPTIONS}
                                    value={STATE_OPTIONS.filter(option => (editingException.rule.states ?? []).includes(option.value))}
                                    onChange={selected => setEditingException(current => ({
                                        ...current,
                                        rule: { ...current.rule, states: (selected ?? []).map(option => option.value) },
                                    }))}
                                />
                                <p className="input-hint">Vazio vale para qualquer estado</p>
                            </div>
                            <div className="col-6">
                                <Input
                                    label={`NCMs`}
                                    value={(editingException.rule.ncms ?? []).join(', ')}
                                    change={value => setEditingException(current => ({
                                        ...current,
                                        rule: {
                                            ...current.rule,
                                            ncms: value.split(',').map(item => item.trim()).filter(Boolean),
                                        },
                                    }))}
                                />
                                <p className="input-hint">Separe por vírgula. Aceita início do NCM: <code>39</code> pega o capítulo inteiro, <code>3924</code> a posição, <code>39241000</code> só aquele item. Vazio vale para qualquer produto</p>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <label className="input-label">Origem da mercadoria</label>
                                <Select
                                    isMulti
                                    placeholder="Qualquer origem"
                                    options={FISCAL_ORIGINS}
                                    value={FISCAL_ORIGINS.filter(option => (editingException.rule.fiscal_origins ?? []).includes(option.value))}
                                    onChange={selected => setEditingException(current => ({
                                        ...current,
                                        rule: { ...current.rule, fiscal_origins: (selected ?? []).map(option => option.value) },
                                    }))}
                                />
                            </div>
                            <div className="col-3">
                                <Input
                                    label={`CFOP`}
                                    value={formatCfop(editingException.rule.cfop)}
                                    change={value => setEditingException(current => ({
                                        ...current,
                                        rule: { ...current.rule, cfop: onlyDigits(value).slice(0, 4) },
                                    }))}
                                />
                                <p className="input-hint">Vazio usa o CFOP da natureza</p>
                            </div>
                        </div>

                        <TaxRuleFields
                            classifications={classifications}
                            tax={editingException.tax}
                            rule={editingException.rule}
                            direction={form.direction}
                            onChange={(kind, key, value) => setEditingException(current => ({
                                ...current,
                                rule: kind === 'attribute'
                                    ? { ...current.rule, attributes: { ...current.rule.attributes, [key]: value } }
                                    : { ...current.rule, [key]: value },
                            }))}
                        />
                    </div>
                }
            </Modal>

            <PanelTemplate id="fiscal-operation-edit">
                <PageHeader
                    left={
                        <>
                            <h1>{isCreating ? 'Nova natureza de operação' : form.name}</h1>
                            <p>Define o CFOP e a tributação das notas emitidas com ela</p>
                        </>
                    }
                    right={
                        <>
                            {!isCreating && !form.is_default && canManage &&
                                <Button
                                    type={`secondary`}
                                    size={`small`}
                                    text={`Tornar padrão`}
                                    action={setAsDefault}
                                    loading={saving}
                                />
                            }
                            <Button
                                type={`secondary`}
                                size={`small`}
                                text={`Voltar`}
                                action={() => navigate(returnTo)}
                            />
                        </>
                    }
                />

                {loading &&
                    <Box><p className={`fiscal-hint`}>Carregando natureza...</p></Box>
                }

                <Box>
                    <div className="section">
                        <div className="row">
                            <div className="col-6">
                                <Input
                                    label={`Nome *`}
                                    value={form.name}
                                    // natOp da NF-e tem até 60 caracteres.
                                    change={value => updateForm({ name: value.slice(0, 60) })}
                                    error={errors.name?.[0]}
                                />
                                <p className="input-hint">Aparece na nota como natureza da operação · {(form.name ?? "").length}/60</p>
                            </div>
                            <div className="col-3">
                                <InputSelect
                                    label={`Tipo *`}
                                    value={form.direction}
                                    change={value => updateForm({ direction: value })}
                                    options={OPERATION_DIRECTIONS}
                                />
                            </div>
                            <div className="col-3">
                                <InputSelect
                                    label={`Finalidade *`}
                                    value={form.purpose}
                                    change={value => updateForm({ purpose: value })}
                                    options={OPERATION_PURPOSES}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-3">
                                <Input
                                    label={`CFOP dentro do estado`}
                                    value={formatCfop(form.cfop_internal)}
                                    change={value => updateForm({ cfop_internal: onlyDigits(value).slice(0, 4) })}
                                    error={errors.cfop_internal?.[0]}
                                    hint={form.direction === 'in' ? 'Começa com 1 (ou 3, exterior)' : 'Começa com 5 (ou 7, exterior)'}
                                />
                            </div>
                            <div className="col-3">
                                <Input
                                    label={`CFOP para outro estado`}
                                    value={formatCfop(form.cfop_interstate)}
                                    change={value => updateForm({ cfop_interstate: onlyDigits(value).slice(0, 4) })}
                                    error={errors.cfop_interstate?.[0]}
                                    hint={form.direction === 'in' ? 'Começa com 2 (ou 3, exterior)' : 'Começa com 6 (ou 7, exterior)'}
                                />
                            </div>
                            <div className="col-6">
                                <InputSelect
                                    label={`Consumidor final`}
                                    value={form.final_consumer}
                                    change={value => updateForm({ final_consumer: value })}
                                    options={FINAL_CONSUMER_OPTIONS}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <InputTextarea
                                    label={`Observações padrões das notas desta operação`}
                                    rows={3}
                                    value={form.notes}
                                    change={value => updateForm({ notes: value })}
                                />
                            </div>
                            <div className="col-6">
                                <InputTextarea
                                    label={`Informações ao Fisco`}
                                    rows={3}
                                    value={form.fisco_notes}
                                    change={value => updateForm({ fisco_notes: value })}
                                />
                            </div>
                        </div>
                    </div>
                </Box>

                <BoxTabs
                    className="fiscal-operation-taxes"
                    titles={OPERATION_TAXES.map(item => item.label)}
                    tab={tab}
                    setTab={setTab}
                    contents={OPERATION_TAXES.map(item => taxTab(item.key))}
                />

                <Box>
                    <div className="section">
                        <h3>Configurações avançadas</h3>

                        {/* Zona Franca, redução da base do ST antes do IPI,
                            desconto da desoneração e movimentação de estoque
                            saíram da tela: nenhuma delas chegava à nota
                            emitida (auditoria fiscal) — voltam quando (e se)
                            forem implementadas de verdade. */}
                        <div className="toggle-row">
                            <Toggle
                                checked={form.settings.items_compose_total !== false}
                                onChange={value => updateSetting('items_compose_total', value)}
                            />
                            <div>
                                <strong>Itens compõem o valor total da nota</strong>
                                <p>Desligue em remessas em que a mercadoria acompanha a nota mas não é cobrada</p>
                            </div>
                        </div>

                        <div className="toggle-row">
                            <Toggle
                                checked={form.active}
                                onChange={value => updateForm({ active: value })}
                            />
                            <div>
                                <strong>Natureza ativa</strong>
                                <p>Inativa some dos seletores da emissão, mas as notas antigas continuam iguais</p>
                            </div>
                        </div>
                    </div>
                </Box>

                {canManage &&
                    <PageFooter>
                        <Button
                            type={`primary`}
                            size={`small`}
                            svg={`save-white`}
                            text={`Salvar natureza`}
                            action={save}
                            loading={saving}
                        />
                        <Button
                            type={`secondary`}
                            size={`small`}
                            text={`Cancelar`}
                            action={() => navigate(returnTo)}
                        />
                    </PageFooter>
                }
            </PanelTemplate>
        </>
    );
};

export default FiscalOperationEdit;
